import { licenses as mockLicenses } from '@/mocks/licenses';
import type { License, LicenseStatus } from '@/domain/types';
import { requireSupabase } from '@/lib/supabase';

export const licenses: License[] = mockLicenses;

export function getLicensesByUserId(userId: string) {
  return licenses.filter((license) => license.userId === userId);
}

export function getLicenseById(id: string) {
  return licenses.find((license) => license.id === id);
}

type LicenseRow = {
  id: string;
  user_id: string;
  order_id: string | null;
  product_id: string;
  plan_id: string | null;
  license_key_last4: string;
  license_key: string | null;
  status: LicenseStatus;
  site_limit: number;
  expires_at: string | null;
  grace_period_ends_at: string | null;
  created_at: string;
  products?: { name?: string | null } | null;
  plans?: { name?: string | null } | null;
  profiles?: { email?: string | null } | null;
  license_activations?: Array<{ id: string; status: string }> | null;
};

const LICENSE_SELECT = `
  *,
  products!product_id(name),
  plans!plan_id(name),
  profiles!user_id(email),
  license_activations!license_id(id, status)
`;

function mapAdminLicense(row: LicenseRow): License {
  return {
    id: row.id,
    userId: row.profiles?.email || row.user_id,
    orderId: row.order_id || '',
    productId: row.product_id,
    planId: row.plan_id || '',
    productName: row.products?.name || row.product_id,
    planName: row.plans?.name || '',
    licenseKeyLast4: row.license_key_last4,
    licenseKey: row.license_key ?? null,
    status: row.status,
    siteLimit: row.site_limit,
    activatedSites: (row.license_activations ?? []).filter(
      (a) => a.status === 'active',
    ).length,
    expiresAt: row.expires_at,
    gracePeriodEndsAt: row.grace_period_ends_at ?? null,
    createdAt: row.created_at,
  };
}

export async function listAdminLicenses(): Promise<License[]> {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('licenses')
    .select(LICENSE_SELECT)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data as LicenseRow[]).map(mapAdminLicense);
}

export async function listCurrentUserLicenses(): Promise<License[]> {
  const supabase = requireSupabase();
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();

  if (userError) throw userError;
  if (!user) return [];

  const { data, error } = await supabase
    .from('licenses')
    .select(LICENSE_SELECT)
    .eq('user_id', user.id)
    .order('created_at', { ascending: false });

  if (error) throw error;

  return ((data as LicenseRow[]) ?? []).map((row) => ({
    ...mapAdminLicense(row),
    userId: user.id,
  }));
}

export async function updateAdminLicenseStatus(
  id: string,
  status: LicenseStatus,
): Promise<License> {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('licenses')
    .update({ status })
    .eq('id', id)
    .select(LICENSE_SELECT)
    .single();

  if (error) throw error;
  return mapAdminLicense(data as LicenseRow);
}

export async function updateAdminLicenseExpiry(
  id: string,
  expiresAt: string | null,
): Promise<License> {
  const supabase = requireSupabase();

  // Auto-compute grace period as expiresAt + 7 days, or null for lifetime.
  const gracePeriodEndsAt = expiresAt
    ? new Date(
        new Date(expiresAt).getTime() + 7 * 24 * 60 * 60 * 1000,
      ).toISOString()
    : null;

  const { data, error } = await supabase
    .from('licenses')
    .update({
      expires_at: expiresAt || null,
      grace_period_ends_at: gracePeriodEndsAt,
      // Reactivate if previously expired
      status: 'active',
    })
    .eq('id', id)
    .select(LICENSE_SELECT)
    .single();

  if (error) throw error;
  return mapAdminLicense(data as LicenseRow);
}

export interface LicenseActivation {
  id: string;
  siteUrl: string;
  siteHost: string;
  status: string;
  pluginVersion: string;
  wpVersion: string;
  phpVersion: string;
  activatedAt: string;
  lastCheckAt: string;
}

export async function getActivationsForLicense(
  licenseId: string,
): Promise<LicenseActivation[]> {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('license_activations')
    .select('id, site_url, site_host, status, plugin_version, wp_version, php_version, activated_at, last_check_at')
    .eq('license_id', licenseId)
    .order('activated_at', { ascending: false });
  if (error) throw error;
  return ((data as any[]) ?? []).map((r) => ({
    id: r.id,
    siteUrl: r.site_url,
    siteHost: r.site_host ?? '',
    status: r.status,
    pluginVersion: r.plugin_version ?? '',
    wpVersion: r.wp_version ?? '',
    phpVersion: r.php_version ?? '',
    activatedAt: r.activated_at,
    lastCheckAt: r.last_check_at,
  }));
}

function asError(err: unknown): Error {
  if (err instanceof Error) return err;
  const msg = (err as any)?.message ?? (err as any)?.error ?? String(err);
  return new Error(msg || 'Request failed.');
}

export async function toggleLicenseStatus(licenseId: string): Promise<void> {
  const supabase = requireSupabase();
  const { error } = await supabase.rpc('customer_toggle_license', {
    p_license_id: licenseId,
  });
  if (error) throw asError(error);
}

export async function deactivateActivation(activationId: string): Promise<void> {
  const supabase = requireSupabase();
  const { error } = await supabase.rpc('customer_deactivate_activation', {
    p_activation_id: activationId,
  });
  if (error) throw asError(error);
}

export async function reactivateActivation(activationId: string): Promise<void> {
  const supabase = requireSupabase();
  const { error } = await supabase.rpc('customer_reactivate_activation', {
    p_activation_id: activationId,
  });
  if (error) throw asError(error);
}

export async function deleteAdminLicense(id: string): Promise<void> {
  const supabase = requireSupabase();
  const { error } = await supabase.from('licenses').delete().eq('id', id);
  if (error) throw error;
}

export interface GenerateLicenseInput {
  productId: string;
  planId: string | null;
  userId: string;
  siteLimit: number;
  expiresAt: string | null;
  status: LicenseStatus;
}

export interface GeneratedLicense {
  id: string;
  licenseKey: string;
  licenseKeyLast4: string;
  productName: string;
}

function makeLicenseKey(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const seg = () => {
    const bytes = new Uint8Array(4);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => chars[b % chars.length]).join('');
  };
  return `${seg()}-${seg()}-${seg()}-${seg()}`;
}

async function hashKey(key: string): Promise<string> {
  const buf = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(key),
  );
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export async function generateAdminLicense(
  input: GenerateLicenseInput,
): Promise<GeneratedLicense> {
  const supabase = requireSupabase();
  const licenseKey = makeLicenseKey();
  const [licenseKeyHash] = await Promise.all([hashKey(licenseKey)]);
  const licenseKeyLast4 = licenseKey.slice(-4);

  const { data, error } = await supabase
    .from('licenses')
    .insert({
      user_id: input.userId,
      product_id: input.productId,
      plan_id: input.planId || null,
      license_key_hash: licenseKeyHash,
      license_key_last4: licenseKeyLast4,
      license_key: licenseKey,
      status: input.status,
      site_limit: input.siteLimit,
      expires_at: input.expiresAt || null,
    })
    .select('id, products!product_id(name)')
    .single();

  if (error) throw error;

  return {
    id: (data as any).id,
    licenseKey,
    licenseKeyLast4,
    productName: (data as any).products?.name || input.productId,
  };
}

export async function getUserLicensesForProduct(
  productId: string,
): Promise<License[]> {
  const supabase = requireSupabase();
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return [];
  const { data, error } = await supabase
    .from('licenses')
    .select(
      `*, products!product_id(name), plans!plan_id(name), license_activations!license_id(id, status)`,
    )
    .eq('user_id', user.id)
    .eq('product_id', productId)
    .in('status', ['active', 'inactive', 'grace'])
    .order('created_at', { ascending: false });
  if (error) throw error;
  return ((data as LicenseRow[]) ?? []).map((row) => ({
    ...mapAdminLicense(row),
    userId: user.id,
  }));
}

// Generates a one-time auth_code stored in connect_tokens.
// The plugin calls the connect-exchange edge function with this code to get the license_key.
export async function connectLicenseToSite(
  licenseId: string,
  domain: string,
  state: string,
  productSlug: string,
  callbackUrl: string,
): Promise<string> {
  const supabase = requireSupabase();

  // Validate site limit before issuing the token
  const { data: lic, error: licErr } = await supabase
    .from('licenses')
    .select('site_limit')
    .eq('id', licenseId)
    .single();
  if (licErr) throw new Error(licErr.message || 'License not found.');

  const { count: activeCount } = await supabase
    .from('license_activations')
    .select('id', { count: 'exact', head: true })
    .eq('license_id', licenseId)
    .eq('status', 'active');

  const siteLimit = (lic as any).site_limit;
  if (siteLimit !== 999 && (activeCount ?? 0) >= siteLimit) {
    throw new Error(
      `License has reached its site limit (${siteLimit} site${siteLimit !== 1 ? 's' : ''}).`,
    );
  }

  const cleanDomain = domain.replace(/^https?:\/\//, '').replace(/\/$/, '');
  const authCode = crypto.randomUUID();

  // Store the token — edge function will exchange it for the license key
  const { error: tokenErr } = await supabase.from('connect_tokens').insert({
    auth_code: authCode,
    license_id: licenseId,
    domain: cleanDomain,
    state,
    product_slug: productSlug,
    callback_url: callbackUrl,
  });
  if (tokenErr)
    throw new Error(tokenErr.message || 'Failed to create connect token.');

  return authCode;
}
